<script lang="ts">
  // BJL-CHECK: Button, Menu + MenuItem, SwitchBare. Choice-panel RadioGroups live in
  // LayersChoicePanel; Bare variants remain because these rows carry custom layouts.
  //
  // `Menu` supplies the drill-down's summary ROWS only; the level itself is this
  // component's, because `mode="drilldown"` cannot host these panels — see the `Panel`
  // block below for what in its source rules it out. `Collapsible` was the other candidate
  // and is the wrong shape: it opens a section in place, which is the inline layout being
  // replaced. Considered and rejected for the rows: `Sidebar`/`Navbar` (page chrome, not a
  // control inside a sheet) and a plain `<button>` (loses the roving tabindex).
  import type { Snippet } from 'svelte'
  import { Button } from '@byjackli-labs/ui/Button'
  import { Menu, MenuItem } from '@byjackli-labs/ui/Menu'
  import { SwitchBare } from '@byjackli-labs/ui/Switch'
  import { Geolocation } from '@capacitor/geolocation'
  import { NativeSettings, AndroidSettings, IOSSettings } from 'capacitor-native-settings'
  import {
    Colors,
    DELAY_BANDS,
    DELAY_UNKNOWN_COLOR,
    DELAY_EARLY_COLOR,
    getRouteColor,
  } from '../../ui/colors'
  import { BACK_ARROW_CHROME, cardChrome, panelFrame, panelTitle, ROW_RULE, Type } from '../../ui/theme'
  import { safeBottom } from '../../ui/safeArea'
  import { useLayersStore, type LayerKey } from '../../store/layers'
  import { useSystemStore } from '../../store/system'
  import { useSettingsStore } from '../../store/settings'
  import { storeSelect, storeState } from '../../lib/store.svelte'
  import { useResizableSheet } from '../../lib/resizableSheet.svelte'
  import { useBackHandler } from '../../hooks/useBackHandler.svelte'
  import LayersChoicePanel from './LayersChoicePanel.svelte'
  import type { LayersLevelId } from './layersSheetOptions'

  // Everything about how the live map looks, in one sheet, so the map's right edge carries a
  // single control instead of a stack of floating buttons. Display settings restyle the
  // moving vehicles; base layers are the map as it has always looked; analysis layers
  // repaint vehicles with backend-derived intelligence; the official MTA map is a static
  // reference opened full-screen.

  interface Row {
    key: LayerKey
    label: string
    hint: string
  }

  const BASE_ROWS: Row[] = [
    { key: 'routeLines', label: 'Route lines', hint: 'Colored track and road geometry' },
    { key: 'stations', label: 'Stations', hint: 'Subway station dots, bullets, and names' },
    { key: 'busStops', label: 'Bus stops', hint: 'Every NYC bus stop, when zoomed in' },
    {
      key: 'buildings',
      label: '3D buildings',
      hint: 'Raise the block around a street-level ride, when zoomed in',
    },
  ]

  const ANALYSIS_ROWS: Row[] = [
    { key: 'delays', label: 'Delays', hint: 'Color each vehicle by how late it is' },
    { key: 'feedHealth', label: 'Feed health', hint: 'Freshness of each upstream MTA feed' },
  ]

  interface Props {
    onClose: () => void
    /** Opens official MTA subway map overlay. */
    onOpenOfficialMap: () => void
    /** Live location permission state. */
    locationGranted: boolean
    /** Triggers OS location permission request. */
    onRequestLocation: () => Promise<boolean>
  }

  const { onClose, onOpenOfficialMap, locationGranted, onRequestLocation }: Props = $props()

  const layers = storeState(useLayersStore)
  // Display settings restyle the moving vehicles. Nothing here has to be cleared against
  // anything else any more: the one setting that did — the Minion skin, a whole-model
  // repaint that could not coexist with `labels` — is a livery now, and a livery just
  // needs a mesh to land on.
  // ── Drill-down ───────────────────────────────────────────────────────────────────────
  //
  // Two of this sheet's settings are pickers of DESCRIBED MODES — display and theme, four
  // prose rows each. Inline they cost more height than everything else on the sheet put
  // together and pushed the base-layer switches, the rows a rider actually comes here to
  // flip, below the fold. So each gets a panel of its own behind a summary row, which is the
  // standard settings shape.
  //
  // ⚠️ The two LIVERY pickers are deliberately NOT levels, even though they are exclusive
  // choices like the other two. A livery is chosen off the picture, and a grid of four
  // vehicle renders is two rows of swatches — it reads at a glance, where four rows of prose
  // have to be read. Hiding a picture behind a row that can only name it (and a summary row
  // does not even do that) spends a tap to show something the rider could already have seen.
  // So they sit inline under their own headings and only the prose pickers drill down.
  //
  // ⚠️ **The SHEET owns the level, not bjl's `Menu`.** `Menu` has a `mode="drilldown"` that
  // is exactly this idea, and it is not usable here, for two reasons found in its source:
  //
  //   • Only `MenuItem` hides itself per level — `isVisibleInDrilldown` lives inside that
  //     component, and `MenuSubmenu` is a bare context wrapper that emits no DOM of its own.
  //     Any other content in a level (a `RadioGroup`, the theme note) therefore renders at
  //     EVERY level, root included. `getMenuContext` would fix that and
  //     is unreachable: the package's export map is `./*` → `./package/*/index.js`, so a
  //     consumer can only import a component directory's barrel, and Menu's barrel exports
  //     the four components plus types.
  //   • `Menu` swaps only its OWN rows. Everything around it on the sheet — the location
  //     card, the display picker, the base-layer switches — would stay on screen beside a
  //     drilled-in level, which is not a panel, it is an accordion.
  //
  // So `Menu`/`MenuItem` do what they can do well — the summary ROWS, with the label,
  // secondary line, chevron and roving-tabindex keyboard nav — and the panel swap below is
  // one `$state`. Android Back pops a level before closing the sheet, which is also only
  // possible this way: `Menu` exposes no imperative handle and no bindable level, so nothing
  // outside it can call its `navigateBack`.
  type Panel = 'root' | LayersLevelId
  let panel = $state<Panel>('root')

  // Every level starts at its top. Without this a rider who scrolled down to reach the
  // summary rows lands mid-panel — or, because a browser clamps `scrollTop` when content
  // shrinks, at the BOTTOM of a panel shorter than the scroll they arrived with.
  let scroller = $state<HTMLDivElement | null>(null)
  $effect(() => {
    panel
    scroller?.scrollTo({ top: 0 })
  })

  useBackHandler(
    () => panel !== 'root',
    () => {
      panel = 'root'
    },
  )

  // The title row doubles as the panel's, so a rider always knows which level they are on.
  const PANEL_TITLES: Record<Panel, string> = {
    root: 'Map options',
    display: 'Vehicle display',
    theme: 'Map theme',
  }

  // Label only. A summary row does NOT echo the setting's current value: the panel behind it
  // opens on that value, already selected, and a picture of the paint is a better answer than
  // its name. Passing `description` is what puts the second line there, so the row carries no
  // `description` prop at all rather than an empty one — bjl only emits the element, and its
  // `aria-describedby` wiring, when the prop is present.
  //
  // `display` first because it is the setting that can empty the others: it decides whether a
  // model is drawn at all, and the livery grids below this plate paint the model it draws.
  // Theme is last because it is the only one that is not about the vehicles.
  const NAV_ROWS: { panel: Panel; label: string }[] = [
    { panel: 'display', label: 'Vehicle display' },
    { panel: 'theme', label: 'Theme' },
  ]

  // The sheet reads exactly one setting itself: the livery caveat below the display picker
  // is about `labels` drawing no model, and only the ROOT level can see both halves of that.
  // Every other setting is read by the LayersChoicePanel that owns its control.
  const vehicleDisplay = storeSelect(useSettingsStore, (s) => s.vehicleDisplay)

  const status = storeSelect(useSystemStore, (s) => s.status)
  const matched = $derived(Math.round((status.value?.delayCoverage ?? 0) * 100))
  const offSchedule = $derived(Math.round((status.value?.delayNonZeroCoverage ?? 0) * 100))

  // Location permission management.
  let perm = $state<'granted' | 'denied' | 'prompt' | 'unknown'>('unknown')

  // checkPermissions() never shows a dialog, so this is cheap on open and whenever
  // `locationGranted` flips.
  $effect(() => {
    locationGranted
    let alive = true
    Geolocation.checkPermissions()
      .then((p) => {
        if (alive) perm = p.location === 'prompt-with-rationale' ? 'prompt' : p.location
      })
      .catch(() => { if (alive) perm = 'unknown' })
    return () => { alive = false }
  })

  const isGranted = $derived(locationGranted || perm === 'granted')
  const needsSettings = $derived(!isGranted && perm === 'denied')

  const locationLabel = $derived(
    isGranted ? 'Location on' : needsSettings ? 'Turn on in Settings' : 'Enable location',
  )
  const locationHint = $derived(
    isGranted
      ? 'Powers the nearby-stations pin and your position on the map'
      : needsSettings
        ? 'Location is off for this app — open Settings to allow it'
        : 'Find nearby stations and see where you are on the map',
  )

  async function handleLocationTap() {
    // Request permission or open system settings.
    if (perm !== 'denied') {
      const ok = await onRequestLocation()
      if (ok) {
        perm = 'granted'
        return
      }
      perm = 'denied'
    }
    try {
      await NativeSettings.open({
        optionAndroid: AndroidSettings.ApplicationDetails,
        optionIOS: IOSSettings.App,
      })
    } catch {
      // Web dev, or no settings surface; nothing more to do.
    }
  }

  // Bottom-anchored flex column with no `height` written, so it SIZES TO ITS CONTENT and is
  // only capped at 75vh — a drill-down level of four radio rows must not open as three
  // quarters of empty black panel over the map. A drag pins a real height (`defaultHeight:
  // null` is what keeps it unpinned until then), and `measureHeight` hands that first drag
  // the height the sheet is actually rendering at.
  let sheetEl = $state<HTMLDivElement | null>(null)
  const sheet = useResizableSheet({
    minHeight: 180,
    defaultHeight: null,
    maxHeight: window.innerHeight * 0.92,
    measureHeight: () => sheetEl?.offsetHeight ?? window.innerHeight * 0.75,
  })
</script>

<!-- SwitchBare renders as the redesign's blue pill with no help from here: catppuccin colours
     it and index.css supplies the geometry the theme omits (see the `.bjl-switch` block
     there). The `layers-check` class is kept as a hook but no longer carries any override:
     the previous version had to hide the track and live with a bare checkbox, and hiding
     `.bjl-switch-switch` now would delete the whole visible control, since index.css makes
     the native input transparent and paints the pill on that very element.

     `.bjl-radio-indicator` is the same wart one component over: the theme styles that circle
     but never hides the native radio beside it, so the row would carry two circles for one
     choice. Here the THEMED circle is the one kept and the native input is hidden (the
     reverse of the switch-era fix) so a radio row speaks the same language as the blue
     pills above it instead of dropping to an OS control mid-sheet. -->
<style>
  /* Made invisible rather than `display: none` so it keeps taking keyboard focus and the
     `<label>` around it still routes taps, the same trick index.css uses on
     `.bjl-switch-input`. */
  :global(.layers-radio .bjl-radio-input) {
    position: absolute;
    width: 20px;
    height: 20px;
    margin: 0;
    opacity: 0;
    pointer-events: none;
  }
  /* Geometry the theme omits: catppuccin paints the indicator's border and the dot's
     colour (from the same `--bjl-color-primary` index.css repaints for the pill) but sizes
     neither, exactly the gap `.bjl-switch` has. 20px matches the switch knob. */
  :global(.layers-radio .bjl-radio-indicator) {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    width: 20px;
    height: 20px;
    border-radius: 50%;
  }
  :global(.layers-radio .bjl-radio-dot) {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    transition: opacity var(--bjl-transition-base, 0.18s ease);
  }
  /* The dot is in the DOM for every option, checked or not, so unchecked has to hide it. */
  :global(.layers-radio .bjl-radio:not(.checked) .bjl-radio-dot) {
    opacity: 0;
  }
  /* The switch rows are 12px control→label with 9px of vertical air; a radio option is
     one of those rows and has to sit on the same grid or the section reads as two lists. */
  :global(.layers-radio .bjl-radio-label) {
    padding: 9px 0;
    align-items: center;
  }
  :global(.layers-radio .bjl-radio-control) {
    align-items: center;
    gap: 12px;
  }
  :global(.layers-radio .bjl-radio-content) {
    gap: 1px;
  }
  :global(.layers-radio .bjl-radio-text) {
    font-size: var(--bjl-font-size-base);
    font-weight: 600;
  }
  :global(.layers-radio .bjl-radio-description) {
    font-size: var(--bjl-font-size-xs);
    font-weight: var(--bjl-font-weight-semibold, 600);
    line-height: var(--bjl-line-height-tight);
    opacity: 1;
    /* The token Colors.textMuted mirrors; catppuccin has no `-text-muted`, and the
       theme's own `opacity: 0.6` on white is not the same grey the hints below use. */
    color: var(--bjl-color-text-disabled);
  }
  /* The switch rows are 12px control→label with 9px of vertical air; a radio option is
     one of those rows and has to sit on the same grid or the section reads as two lists. */
  :global(.layers-radio .bjl-radio-label) {
    padding: 9px 0;
    align-items: center;
  }
  :global(.layers-radio .bjl-radio-control) {
    align-items: center;
    gap: 12px;
  }
  :global(.layers-radio .bjl-radio-content) {
    gap: 1px;
  }
  :global(.layers-radio .bjl-radio-text) {
    font-size: var(--bjl-font-size-base);
    font-weight: 600;
  }
  :global(.layers-radio .bjl-radio-description) {
    font-size: var(--bjl-font-size-xs);
    font-weight: var(--bjl-font-weight-semibold, 600);
    line-height: var(--bjl-line-height-tight);
    opacity: 1;
    /* The token Colors.textMuted mirrors; catppuccin has no `-text-muted`, and the
       theme's own `opacity: 0.6` on white is not the same grey the hints below use. */
    color: var(--bjl-color-text-disabled);
  }

  /* ── The drill-down's summary rows ────────────────────────────────────────────────
     ⚠️ Catppuccin ships **no `Menu.css`** — only cloudscape and sadforms have one, and
     `Menu.svelte`'s own `<style>` is the empty "theme styles are in ui-themes" stub every
     bjl component carries. So unlike `Switch` and `RadioGroup`, where the theme paints and
     only the geometry is missing, a `Menu` here arrives with NOTHING: bare `<button>`s in
     the UA's font. Everything below is the skin, not an override, and it is deliberately
     the `toggle` snippet's row geometry to the pixel — these rows sit in the same plate as
     the base-layer switches, and a settings list that changes metrics halfway down reads as
     two lists. If catppuccin ever ships Menu.css, re-check this block rather than deleting
     it: what it would supply is colour, and the 12px/9px grid would still be ours. */
  :global(.layers-menu) {
    display: flex;
    flex-direction: column;
  }
  :global(.layers-menu .bjl-menu-item) {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    padding: 12px 0;
    border: none;
    /* `ROW_RULE`, which a `<style>` block cannot read — it is built from `Colors.border`,
       and that constant mirrors this token. Keep the two in step. */
    border-bottom: 1px solid var(--bjl-color-border, rgba(255, 255, 255, 0.08));
    background: none;
    font: inherit;
    text-align: left;
    cursor: pointer;
  }
  /* The label is the row's only content, so it takes the free space and the chevron below
     rides the far edge. ⚠️ There is no `.bjl-menu-item-content` wrapper to hang that on:
     bjl emits one only when an item has a `description`, and drops straight to a bare label
     span otherwise. Give these rows a description again and this rule stops reaching the
     text — the wrapper becomes the flex child. */
  :global(.layers-menu .bjl-menu-item-label) {
    flex: 1;
    min-width: 0;
    color: var(--bjl-color-text, #fff);
    font-size: var(--bjl-font-size-base);
    font-weight: 600;
  }
  /* The chevron is ours because bjl only draws one on an item that OWNS a submenu, and
     these are leaves — the panel they open is the sheet's, not the menu's. Same glyph and
     the same grey as the location card and the MTA-map button, which are the other two rows
     on this sheet that go somewhere. */
  :global(.layers-menu .bjl-menu-item::after) {
    content: '›';
    flex-shrink: 0;
    color: rgba(255, 255, 255, 0.35);
    font-size: 18px;
    line-height: 1;
  }
  /* ⚠️ This ring hangs off `:focus-visible`, NOT bjl's `.bjl-menu-item--focused`. That class
     was the obvious hook and it is wrong here: `MenuItem` gates it on `hasInteracted && isFocused`,
     and `Menu`'s `focusin` handler sets `hasInteracted` for a TAP as readily as for a Tab. So on
     a phone the first row a rider touched — "Vehicle display", the first item — kept a blue
     outline for as long as it held focus, which is until something else takes it. A pointer
     press is not a request to be shown where the keyboard is. `:focus-visible` is the browser's
     own answer to exactly that question, so keyboard users still get the ring and no one else
     ever sees it. */
  :global(.layers-menu .bjl-menu-item:focus-visible) {
    outline: 2px solid var(--bjl-color-primary);
    outline-offset: -2px;
    border-radius: 6px;
  }

  /* ── The livery picker's card row ─────────────────────────────────────────────────
     Everything below undoes the ROW geometry above for this one group, so the selector
     carries all three classes the element has (`.bjl-radiogroup.layers-radio.layers-livery`,
     0-3-0). That is not belt and braces: bjl's own card and snippet rules are 0-3-0 too
     (`.bjl-radiogroup.v-card .bjl-radio-label`, `.bjl-radio-label.has-radio-snippet
     .bjl-radio-control`), and a tie would be settled by whichever stylesheet the bundler
     happened to emit last. */
  /* The gutters are held tight because they are the only thing between the swatches and the
     plate's edges: on a 360px handset there is ~320px to divide, and every pixel spent on a
     gap or a card's inset comes straight off a picture that is already half of that.
     `align-items: stretch` is what keeps a row level when one caption wraps to two lines and
     its neighbour doesn't. */
  :global(.bjl-radiogroup.layers-radio.layers-livery) {
    gap: 6px;
    padding: 6px 0 10px;
    align-items: stretch;
  }
  /* TWO per row on a phone, 2 × 2 for either fleet's four schemes. Four across fits, but a
     quarter of a 360px handset's plate is a ~63px swatch — a vehicle drawn 63px long is a
     coloured smear, and a livery is chosen off the picture. Half the plate is ~145px, which
     is a bus you can actually read.

     The basis subtracts its own share of the `gap` (one 6px gutter across two columns, three
     across four), so a row lands exactly on the plate with no cap needed — the columns track
     the sheet at every width and `LIVERY_PREVIEW_ASPECT` turns each one's width into its
     swatch's height. `flex-grow: 0` is the load-bearing half: with grow, a row holding one
     leftover card would stretch it to the full plate and draw that scheme at twice everyone
     else's size. Both fleets offer exactly four today (`LIVERY_IDS_FOR`: the trains take
     as-built where the buses take Classic), so neither grid has a leftover — a FIFTH livery
     is what would, and it costs nothing because of this line. */
  :global(.bjl-radiogroup.layers-radio.layers-livery .bjl-radio) {
    flex: 0 1 calc(50% - 3px);
    min-width: 0;
    max-width: none;
  }
  /* A sheet this wide is a browser window, not a handset — it is `left: 0; right: 0`, so its
     width IS the viewport's. By here a quarter of the plate is already wider than the half a
     phone gives, so all four go on one line and the whole choice is visible at once. */
  @media (min-width: 500px) {
    :global(.bjl-radiogroup.layers-radio.layers-livery .bjl-radio) {
      flex-basis: calc(25% - 4.5px);
    }
  }
  /* The card IS the label, so it takes the whole column — including the height the row's
     tallest caption sets, which is what keeps the cards in a row even. */
  :global(.bjl-radiogroup.layers-radio.layers-livery .bjl-radio-label) {
    width: 100%;
    height: 100%;
    box-sizing: border-box;
    padding: 6px;
    align-items: stretch;
    background: var(--bjl-color-surface-alt);
  }
  /* Picture over caption. bjl's snippet default reserves 12rem for the text beside the
     indicator, which would take that width out of a swatch that is meant to span the plate. */
  :global(
    .bjl-radiogroup.layers-radio.layers-livery
      .bjl-radio-label.has-radio-snippet
      .bjl-radio-control
  ) {
    flex: 1 1 100%;
    min-width: 0;
    flex-direction: column;
    align-items: stretch;
    gap: 7px;
  }
  /* The indicator is the swatch's frame now, not the 20px dot the rows above use. */
  :global(.bjl-radiogroup.layers-radio.layers-livery .bjl-radio-indicator) {
    display: block;
    width: 100%;
    height: auto;
    border-radius: 0;
  }
  :global(.bjl-radiogroup.layers-radio.layers-livery .bjl-radio-text) {
    font-size: var(--bjl-font-size-sm);
  }
  :global(.bjl-radiogroup.layers-radio.layers-livery .bjl-radio-content) {
    gap: 2px;
  }
  /* Kept in the DOM and taken out of the picture. The notes were written for a full-width
     row and run to five lines each in a half-width tile, and beside a render of the vehicle
     wearing the paint, prose describing the paint earns none of that height. Clipped rather
     than `display: none` on purpose — this element is bjl's `aria-describedby` target, and
     hidden text is the case screen readers are least consistent about. */
  :global(.bjl-radiogroup.layers-radio.layers-livery .bjl-radio-description) {
    position: absolute;
    width: 1px;
    height: 1px;
    margin: -1px;
    padding: 0;
    overflow: hidden;
    clip-path: inset(50%);
    white-space: nowrap;
  }

</style>

{#snippet legendRow(color: string, label: string)}
  <div style="display: flex; align-items: center; gap: 7px;">
    <span
      style="width: 10px; height: 10px; border-radius: 5px; background-color: {color}; display: inline-block; flex-shrink: 0;"
    ></span>
    <span style="color: {Colors.textSecondary}; {Type.caption};">{label}</span>
  </div>
{/snippet}

<!-- Delay legend showing delay status compared to scheduled arrival times. -->
{#snippet delayLegend()}
  <div style="display: flex; flex-direction: column; gap: 5px; padding-left: 2px;">
    <div style="display: flex; flex-wrap: wrap; gap: 5px 14px;">
      {@render legendRow(DELAY_EARLY_COLOR, 'Running early')}
      {#each DELAY_BANDS as b (b.label)}
        {@render legendRow(b.color, b.label)}
      {/each}
      {@render legendRow(DELAY_UNKNOWN_COLOR, 'Not scheduled')}
    </div>
    {#if status.value}
      <span
        style="color: {matched === 0 ? Colors.warning : Colors.textMuted}; {Type.caption}; line-height: 1.35;"
      >
        {matched === 0
          ? 'No schedule data is loaded right now, so no train can be checked against its timetable.'
          : `Measured against the published timetable, not reported by the MTA. ` +
            `${offSchedule}% of trains are running more than a minute off schedule. ` +
            `The ${100 - matched}% shown grey are running trips that aren’t in the schedule at all.`}
      </span>
    {/if}
  </div>
{/snippet}

<!-- Switch row template -->
{#snippet toggle(
  key: string,
  label: string,
  hint: string,
  active: boolean,
  onToggle: () => void,
  icon?: Snippet,
  last = false,
)}
  {@const id = `layer-switch-${key}`}

  <div
    style="
      display: flex; align-items: center; gap: 12px; width: 100%; padding: 12px 0;
      {last ? '' : `border-bottom: ${ROW_RULE};`}
    "
  >
    <label for={id} style="flex: 1; min-width: 0; cursor: pointer;">
      <span style="display: flex; align-items: center; gap: 6px;">
        <span style="color: {Colors.text}; {Type.bodyLg}; font-weight: 600;">{label}</span>
        {@render icon?.()}
      </span>
      <span style="display: block; color: {Colors.textMuted}; {Type.caption}; margin-top: 2px;">
        {hint}
      </span>
    </label>
    <SwitchBare class="layers-check" {id} checked={active} onchange={onToggle} />
  </div>
{/snippet}

{#snippet sectionHeading(text: string)}
  <div
    style="color: {Colors.textMuted}; {Type.eyebrow}; padding: 18px 0 8px;"
  >{text}</div>
{/snippet}

<div
  bind:this={sheetEl}
  style="
    position: fixed; bottom: 0; left: 0; right: 0;
    background-color: {Colors.ground};
    {panelFrame('top')}
    {sheet.height == null ? 'max-height: 75vh;' : `height: ${sheet.height}px; max-height: 92vh;`}
    z-index: 1000;
    display: flex; flex-direction: column;
  "
>
  <!-- Drag handle for resizing sheet -->
  <div
    onpointerdown={sheet.onHandleDown}
    role="separator"
    aria-label="Resize map options"
    style="
      flex-shrink: 0; display: flex; justify-content: center;
      padding: 8px 0 4px; cursor: ns-resize; touch-action: none;
    "
  >
    <span style="width: 36px; height: 4px; border-radius: 2px; background-color: rgba(255,255,255,0.25);"></span>
  </div>

  <!-- Symmetric vertical padding: the scroller below clips at this row's bottom edge, so an
       asymmetric gutter made rows slide right up under the title while the list scrolls.

       This row is also the drill-down's chrome: the title names the level and the back arrow
       appears beside it, rather than a back row inside the scroller — a level's title
       belongs where the sheet's title is, and a back control that scrolls away is one a
       rider has to hunt for. ✕ keeps its place at every level and always closes the sheet;
       the two never swap sides. -->
  <div style="padding: 10px 20px; display: flex; align-items: center; gap: 4px; flex-shrink: 0;">
    {#if panel !== 'root'}
      <Button
        variant="text"
        size="sm"
        iconOnly
        onclick={() => (panel = 'root')}
        aria-label="Back to map options"
        title="Back to map options"
        style={BACK_ARROW_CHROME}
      >‹</Button>
    {/if}
    <!-- The title takes the row's free space so ✕ stays pinned right. It does not shrink —
         a level's name is not a thing to ellipsise. -->
    <span style="flex: 1; min-width: 0; {panelTitle(22)};">{PANEL_TITLES[panel]}</span>
    <Button variant="text" size="sm" iconOnly onclick={onClose} aria-label="Close map options">✕</Button>
  </div>

  <div
    bind:this={scroller}
    style="
      flex: 1; min-height: 0;
      overflow-y: auto; padding: 0 20px {safeBottom(20)};
      display: flex; flex-direction: column;
    "
  >
    {#if panel !== 'root'}
      <!-- A panel is the picker alone. It carries no heading of its own — the title row above
           is the heading, and repeating it here would be the only text on the level. -->
      <div style="{cardChrome()} padding: 6px 14px; margin-top: 16px;">
        <LayersChoicePanel choice={panel} />
      </div>
    {:else}
    <!-- Location permission status -->
    {#if isGranted}
      <!-- Granted is a status, not an action, so it renders inert and doesn't invite a tap
           that does nothing. A tick stands in for the switch/chevron the other states carry. -->
      <div style="{cardChrome()} display: flex; align-items: center; gap: 12px; padding: 12px 14px; margin-top: 16px;">
        <span style="font-size: 18px; flex-shrink: 0;">📍</span>
        <span style="flex: 1; min-width: 0;">
          <span style="display: block; color: {Colors.text}; {Type.bodyLg}; font-weight: 600;">
            {locationLabel}
          </span>
          <span style="display: block; color: {Colors.textMuted}; {Type.caption}; margin-top: 2px;">
            {locationHint}
          </span>
        </span>
        <span style="color: {Colors.primary}; font-size: 15px; flex-shrink: 0;">✓</span>
      </div>
    {:else}
      <button
        onclick={handleLocationTap}
        style="
          {cardChrome()} display: flex; align-items: center; gap: 12px; width: 100%;
          border: none; cursor: pointer; padding: 12px 14px; margin-top: 16px; text-align: left;
        "
      >
        <span style="font-size: 18px; flex-shrink: 0;">📍</span>
        <span style="flex: 1; min-width: 0;">
          <span style="display: block; color: {Colors.text}; {Type.bodyLg}; font-weight: 600;">
            {locationLabel}
          </span>
          <span style="display: block; color: {Colors.textMuted}; {Type.caption}; margin-top: 2px;">
            {locationHint}
          </span>
        </span>
        <span style="color: rgba(255,255,255,0.35); font-size: 18px; flex-shrink: 0;">›</span>
      </button>
    {/if}

    <!-- Pickers and Base in one plate: from the rider's side these are all "what the map
         draws", and the redesign shows them as a single ruled group. The pickers are ruled
         rows in this same plate rather than divider()-separated sections of their own, which
         is why there is no divider() snippet here. -->
    {@render sectionHeading('What the map shows')}
    <div style="{cardChrome()} padding: 2px 14px;">
      <!-- The two prose pickers, collapsed to summary rows, above the booleans they share a
           plate with: a rider reads one list of things the map draws, and a row's control
           says whether the answer is yes/no or one-of-several. (The exclusive/boolean split
           is not the whole rule for what becomes a level — the livery grids below this plate
           are exclusive too and stay inline, because they answer with a picture.)

           `Menu` is a plain list here — its own `mode="drilldown"` cannot host these panels
           (see the note in the script) — so every item is a leaf with an `onclick` that sets
           the level, and the chevron and roving-tabindex keyboard nav come from `MenuItem`.

           `role="menu"` is what the component gives them and is the one thing being accepted
           rather than chosen: these rows navigate rather than command. It is the same trade
           `Tag` takes for a bus badge — the library's chrome over the app's data — and the
           alternative is four hand-rolled buttons duplicating the rows below them. -->
      <Menu class="layers-menu" ariaLabel="Vehicle and map appearance">
        {#each NAV_ROWS as row (row.panel)}
          <MenuItem
            id="layers-nav-{row.panel}"
            label={row.label}
            onclick={() => (panel = row.panel)}
          />
        {/each}
      </Menu>
      {#each BASE_ROWS as row, i (row.key)}
        {@render toggle(
          row.key,
          row.label,
          row.hint,
          layers.value[row.key],
          () => layers.value.toggleLayer(row.key),
          undefined,
          i === BASE_ROWS.length - 1,
        )}
      {/each}
    </div>

    <!-- The two paint grids, inline and each under its own heading. They sit directly below
         the plate holding Vehicle display for the reason that picker's summary row used to
         sit above them: `display` is what decides whether there is a model here to paint.

         Each fleet gets a plate of its own rather than one plate with two headings inside it,
         because a section heading is this sheet's plate separator everywhere else on the
         level and the grids are two independent settings, not two halves of one. -->
    {#if vehicleDisplay.value === 'labels'}
      <!-- The one cross-setting caveat on this sheet. Inline it can be shown only when it is
           TRUE — a livery needs a mesh to land on and `labels` draws none — where the panel
           version had to be permanent chrome on both levels, since a level cannot see the
           setting that emptied it without the rider going back a step. -->
      <div style="color: {Colors.warning}; {Type.caption}; line-height: 1.35; padding: 18px 0 0;">
        Note: Labels mode draws no vehicle = no livery to paint
      </div>
    {/if}
    {@render sectionHeading('Train livery')}
    <div style="{cardChrome()} padding: 2px 14px;">
      <LayersChoicePanel choice="subway" />
    </div>

    {@render sectionHeading('Bus livery')}
    <div style="{cardChrome()} padding: 2px 14px;">
      <LayersChoicePanel choice="bus" />
    </div>

    <!-- Analysis stays its own plate: these two repaint the vehicles with backend-derived
         intelligence rather than showing or hiding something, and one of them opens a legend
         underneath itself, which cannot sit mid-card without breaking the rules. -->
    {@render sectionHeading('Analysis')}
    <div style="{cardChrome()} padding: 2px 14px;">
      {#each ANALYSIS_ROWS as row, i (row.key)}
        {@const isLast = i === ANALYSIS_ROWS.length - 1}
        {@render toggle(
          row.key,
          row.label,
          row.hint,
          layers.value[row.key],
          () => layers.value.toggleLayer(row.key),
          undefined,
          isLast && !(row.key === 'delays' && layers.value.delays),
        )}
        <!-- The legend appears only under its own active layer: a color key is useless
             when the colors aren't on the map, and noisy when the layer is off. -->
        {#if row.key === 'delays' && layers.value.delays}
          <div style="padding: 0 0 12px; {isLast ? '' : `border-bottom: ${ROW_RULE};`}">
            {@render delayLegend()}
          </div>
        {/if}
      {/each}
    </div>

    <!-- Official MTA subway map button -->
    <button
      onclick={onOpenOfficialMap}
      style="
        {cardChrome()} display: flex; align-items: center; gap: 12px; width: 100%;
        border: none; cursor: pointer; padding: 12px 14px; text-align: left;
        margin-top: 26px; flex-shrink: 0;
      "
    >
      <span style="font-size: 20px; flex-shrink: 0;">🗺</span>
      <span style="flex: 1; min-width: 0;">
        <span style="display: block; color: {Colors.text}; {Type.bodyLg}; font-weight: 600;">
          MTA subway map
        </span>
        <span style="display: block; color: {Colors.textMuted}; {Type.caption}; margin-top: 2px;">
          Official full-system diagram
        </span>
      </span>
      <span style="color: rgba(255,255,255,0.35); font-size: 18px; flex-shrink: 0;">›</span>
    </button>

    <!-- Reset sits on the sheet's own bottom edge, full width, the way the redesign closes
         the panel. There is no "Apply" beside it (the mockup's pair): every toggle here
         takes effect on the map the instant it flips, so a confirm button would be a
         no-op that implies the changes hadn't happened yet. -->
    <Button
      variant="secondary"
      onclick={() => layers.value.resetLayers()}
      style="margin-top: 20px; width: 100%; border-radius: 10px; padding: 12px 0;"
    >
      Reset to defaults
    </Button>
    {/if}
  </div>
</div>
